import { useEffect, useState } from 'react';

import {
  applyDocumentPreviewResize,
  clampDocumentPreviewDialogLayout,
  getDocumentPreviewDialogLayout,
  getDocumentPreviewDragBounds,
  getDocumentPreviewSizeConstraints,
  isSameDocumentPreviewDialogLayout,
} from './utils';

import type { ResizeHandle } from 'react-resizable';
import type {
  DocumentPreviewDragHandler,
  DocumentPreviewResizeHandler,
} from './types';

export default function useDocumentPreviewDialogLayout(open: boolean) {
  const [layout, setLayout] = useState(getDocumentPreviewDialogLayout);
  const [layoutForOpen, setLayoutForOpen] = useState(open);
  const [resizeHandle, setResizeHandle] = useState<ResizeHandle>();
  const [viewport, setViewport] = useState(() => ({
    height: window.innerHeight,
    width: window.innerWidth,
  }));

  if (open !== layoutForOpen) {
    setLayoutForOpen(open);
    setResizeHandle(undefined);
    if (open) {
      setLayout(getDocumentPreviewDialogLayout());
      setViewport({ height: window.innerHeight, width: window.innerWidth });
    }
  }

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleViewportResize = () => {
      const nextViewport = { height: window.innerHeight, width: window.innerWidth };
      setViewport((current) =>
        current.width === nextViewport.width && current.height === nextViewport.height
          ? current
          : nextViewport,
      );
      setLayout((current) => {
        const next = clampDocumentPreviewDialogLayout(
          current,
          nextViewport.width,
          nextViewport.height,
        );
        return isSameDocumentPreviewDialogLayout(current, next) ? current : next;
      });
    };

    window.addEventListener('resize', handleViewportResize);
    return () => window.removeEventListener('resize', handleViewportResize);
  }, [open]);

  const handleDrag: DocumentPreviewDragHandler = (_event, data) => {
    setLayout((current) => {
      const next = clampDocumentPreviewDialogLayout({ ...current, x: data.x, y: data.y });
      return isSameDocumentPreviewDialogLayout(current, next) ? current : next;
    });
  };

  const handleResizeStart: DocumentPreviewResizeHandler = (_event, { handle }) => {
    setResizeHandle(handle);
  };

  const handleResize: DocumentPreviewResizeHandler = (_event, { handle, size }) => {
    setLayout((current) => {
      const next = applyDocumentPreviewResize(current, size, handle);
      return isSameDocumentPreviewDialogLayout(current, next) ? current : next;
    });
  };

  const handleResizeStop: DocumentPreviewResizeHandler = () => {
    setResizeHandle(undefined);
  };

  const { maxHeight, maxWidth, minHeight, minWidth } = getDocumentPreviewSizeConstraints(
    layout,
    viewport.width,
    viewport.height,
    resizeHandle,
  );

  return {
    bounds: getDocumentPreviewDragBounds(layout, viewport.width, viewport.height),
    handleDrag,
    handleResize,
    handleResizeStart,
    handleResizeStop,
    height: layout.height,
    maxHeight,
    maxWidth,
    minHeight,
    minWidth,
    width: layout.width,
    x: layout.x,
    y: layout.y,
  };
}
